import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS, apiRequest } from '../../../api/config';
import { useTheme } from '../ThemeContext';
import { getTranslation } from '../translations';

// 导入样式文件
import '../../../styles/dashboard.css';
import '../../../styles.css';

// Toast样式
const toastStyles = `
@keyframes toast-slide-in {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
.cloak-toast {
  position: fixed; top: 20px; right: 20px; z-index: 10000;
  background: var(--bg-card); border: 1px solid var(--border);
  border-radius: 10px; padding: 12px 20px; font-size: 13px;
  display: flex; align-items: center; gap: 8px;
  animation: toast-slide-in 0.3s ease; box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}
.cloak-toast-icon { width: 18px; height: 18px; flex-shrink: 0; }
`;

// 统一价格分档特性函数 — 与 LandingPage Pricing / CloakPlans 完全一致
// 档位: 体验版($0) / 入门版($299) / 成长版($799) / 专业版($1999) / 旗舰版($3999)
const getPlanFeatures = (plan, lang = 'zh') => {
  const features = [];
  const price = Number(plan.price) || 0;
  const totalCalls = plan?.totalCalls ?? 0;
  const isUnlimitedCalls = totalCalls === 0;

  // 地理定向: $500以下=基础，$500+=全球
  if (price < 500) {
    features.push({ text: lang === 'zh' ? '基础地理定向' : 'Basic Geo-Targeting', enabled: true });
  } else {
    features.push({ text: lang === 'zh' ? '全球地理定向' : 'Global Geo-Targeting', enabled: true });
  }
  features.push({ text: lang === 'zh' ? 'UA 识别拦截' : 'UA Detection & Block', enabled: true });

  // 访问日志: 1天→30天→90天→180天→365天
  if (isUnlimitedCalls || price >= 3999) {
    features.push({ text: lang === 'zh' ? '访问日志 365 天' : 'Access Logs 365 days', enabled: true });
  } else if (price >= 1999) {
    features.push({ text: lang === 'zh' ? '访问日志 180 天' : 'Access Logs 180 days', enabled: true });
  } else if (price >= 799) {
    features.push({ text: lang === 'zh' ? '访问日志 90 天' : 'Access Logs 90 days', enabled: true });
  } else if (price >= 299) {
    features.push({ text: lang === 'zh' ? '访问日志 30 天' : 'Access Logs 30 days', enabled: true });
  } else if (price > 0) {
    features.push({ text: lang === 'zh' ? '访问日志 30 天' : 'Access Logs 30 days', enabled: true });
  } else {
    features.push({ text: lang === 'zh' ? '访问日志 1 天' : 'Access Logs 1 day', enabled: true });
  }

  // 斗篷 + API + 子账号
  if (price >= 3999) {         // 旗舰版 $3999
    features.push({ text: lang === 'zh' ? '无限斗篷数量' : 'Unlimited Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '10 个子账号' : '10 Sub-accounts', enabled: true });
  } else if (price >= 1999) {  // 专业版 $1999
    features.push({ text: lang === 'zh' ? '最多 300 个斗篷' : 'Up to 300 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '3 个子账号' : '3 Sub-accounts', enabled: true });
  } else if (price >= 799) {   // 成长版 $799
    features.push({ text: lang === 'zh' ? '最多 100 个斗篷' : 'Up to 100 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: true });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  } else if (price >= 299) {   // 入门版 $299
    features.push({ text: lang === 'zh' ? '最多 10 个斗篷' : 'Up to 10 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: false });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  } else {                     // 体验版 $0
    features.push({ text: lang === 'zh' ? '最多 5 个斗篷' : 'Up to 5 Cloaks', enabled: true });
    features.push({ text: lang === 'zh' ? 'API 接入' : 'API Access', enabled: false });
    features.push({ text: lang === 'zh' ? '子账号' : 'Sub-accounts', enabled: false });
  }

  return features;
};

const CloakPricing = () => {
  const { language } = useTheme();
  const t = getTranslation(language);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  
  // 防止 StrictMode 双重执行
  const lastDepsRef = useRef(null);

  // 支付弹窗相关状态
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentData, setPaymentData] = useState({
    remark: '',
    screenshot: null
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Toast状态
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  // 钱包地址
  const walletAddress = 'TPUC9TvsbMxS2NoAAahMtfxqaPbpUHVSqi';

  // 注入Toast动画样式
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'pricing-toast-animations';
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);
    return () => {
      const existing = document.getElementById('pricing-toast-animations');
      if (existing) existing.remove();
    };
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
  };

  useEffect(() => {
    // 防止 StrictMode 开发环境下重复调用
    if (lastDepsRef.current === language) return;
    lastDepsRef.current = language;

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const result = await apiRequest(API_ENDPOINTS.PLAN.LIST, {}, true);
        const plansData = result?.data || result?.items || [];
        if (plansData.length > 0) {
          // 按金额从小到大排序
          setPlans(plansData.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0)));
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load plans:', err);
        setError(t('loadPlansFailed'));
        setLoading(false);
      }
    };
    fetchPlans();
  }, [language]);

  // 处理从主页面跳转过来的购买请求：?buy={planId} → 自动打开支付弹窗
  useEffect(() => {
    const buyPlanId = searchParams.get('buy');
    if (buyPlanId && plans.length > 0) {
      const plan = plans.find(p => p.id === buyPlanId || p.id === Number(buyPlanId));
      if (plan) {
        setSelectedPlan(plan.id);
        setShowPaymentModal(true);
      }
    }
  }, [searchParams, plans]);

  // 点击"立即购买" → 直接打开支付弹窗
  const handleBuyClick = (planId) => {
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };

  // 取消支付
  const handleCancelPayment = () => {
    setShowPaymentModal(false);
    setPaymentData({ remark: '', screenshot: null });
  };

  // 文件上传
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentData({ ...paymentData, screenshot: e.target.files[0] });
    }
  };

  // 文本输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({ ...paymentData, [name]: value });
  };

  // 提交支付
  const handlePaymentSubmit = async () => {
    try {
      if (!paymentData.screenshot) {
        showToast(t('pleaseUploadPaymentScreenshot'), 'error');
        return;
      }

      setSubmitting(true);
      const formData = new FormData();
      formData.append('planId', selectedPlan);
      formData.append('remark', paymentData.remark || '');
      formData.append('screenshot', paymentData.screenshot);

      const result = await apiRequest(
        API_ENDPOINTS.PLAN.SUBMIT_PAYMENT,
        formData,
        true,
        'POST',
        true
      );

      if (result && result.status === 'success') {
        setShowPaymentModal(false);
        setPaymentData({ remark: '', screenshot: null });
        setShowSuccessModal(true);
      } else {
        throw new Error(result.error || t('failedToSubmitPaymentInformation'));
      }
    } catch (error) {
      console.error('Failed to submit payment:', error);
      showToast(error.message || t('failedToSubmitPaymentInformation'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-state">
        <p className="page-state-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="page active">
      <div className="page-hd">
        <div>
          <div className="page-title">{t('planPurchase')}</div>
          <div className="page-sub">{t('purchaseSubtitle')}</div>
        </div>
      </div>

      {plans.length > 0 ? (
        <div className="plan-grid">
          {plans.map((plan) => {
            const totalCalls = plan?.totalCalls ?? 0;
            const isUnlimited = totalCalls === 0;
            const avgPrice = isUnlimited ? '-' : (plan.price / totalCalls).toFixed(4);
            const isFeatured = plan.featured || plan.recommended || false;

            return (
              <div key={plan.id} className={`plan-card ${isFeatured ? 'featured' : ''}`}>
                {isFeatured && (
                  <span style={{
                    position: 'absolute', top: '12px', right: '-24px',
                    background: 'var(--accent)', color: '#fff',
                    fontSize: '10px', padding: '3px 32px', transform: 'rotate(45deg)'
                  }}>
                    {t('recommended')}
                  </span>
                )}

                <div className="plan-name">{language === 'zh' ? plan.name : (plan.nameEn || plan.name)}</div>

                <div className="plan-price">
                  <sup>$</sup>{plan.price}
                </div>

                {plan.validityDays && !plan.randomPeriod && (
                  <div className="plan-period">
                    {plan.validityDays}{t('daysText')}{t('validPeriod')}
                  </div>
                )}

                {!isUnlimited && (
                  <div className="plan-avg">
                    {t('avgPrice')} ${avgPrice} / {t('perCall')}
                  </div>
                )}

                <div className="plan-quota">
                  {isUnlimited ? t('unlimited') : totalCalls.toLocaleString()}
                </div>
                <div className="plan-quota-label">{t('realLinkCalls')}</div>

                <div className="plan-features">
                  {/* 使用统一价格分档特性 — 与 LandingPage / CloakPlans 完全一致 */}
                  {/* TODO: 后续放开子账号功能 */}
                  {getPlanFeatures(plan, language).filter(f => !f.text.includes('子账号')).map((feat, idx) => (
                    <div key={idx} className={`plan-feat ${!feat.enabled ? 'plan-feat-disabled' : ''}`}>
                      <span className={feat.enabled ? 'check' : 'cross'}>{feat.enabled ? '✓' : '✕'}</span>
                      <span style={{ opacity: feat.enabled ? 1 : 0.45 }}>{feat.text}</span>
                    </div>
                  ))}
                </div>

                <button className="plan-buy-btn" onClick={() => handleBuyClick(plan.id)}>
                  {t('buyNow')}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="page-state">
          <p className="page-state-text">{t('noPlanData')}</p>
        </div>
      )}

      {/* 支付说明 */}
      <div className="table-wrap mt-14" style={{ padding: '16px', fontSize: '12px', color: 'var(--t2)', lineHeight: '2.2' }}>
        <strong style={{ color: 'var(--t1)' }}>{t('paymentMethods')}</strong>
        💳 USDT（TRC20/ERC20）&nbsp;&nbsp;
        💳 {t('creditCard')}&nbsp;&nbsp;
        🏦 {t('bankTransfer')}<br />
        <strong style={{ color: 'var(--t1)' }}>{t('paymentNote')}</strong>
        {t('paymentDescription')}
      </div>

      {/* 支付信息弹窗 - 纯内联样式，零CSS依赖 */}
      {showPaymentModal && (
        <div
          onClick={handleCancelPayment}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#1a1a2e', border: '1px solid #2d2d44',
            borderRadius: '12px', width: '520px', maxWidth: '94vw',
            maxHeight: '88vh', overflowY: 'auto'
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #2d2d44' }}>
              <span style={{ fontSize: '16px', fontWeight: 600 }}>{t('paymentInformation')}</span>
              <span onClick={handleCancelPayment} style={{ float: 'right', cursor: 'pointer', fontSize: '24px' }}>×</span>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', color: '#888' }}>
                  {t('selectedPlanText', {
                    planName: (() => {
                      const selectedPlanObj = plans.find(p => p.id === selectedPlan);
                      return language === 'zh' ?
                        selectedPlanObj?.name :
                        (selectedPlanObj?.nameEn || selectedPlanObj?.name);
                    })()
                  })}
                </p>
              </div>

              {/* 钱包地址 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>{t('walletAddress')}</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="text" readOnly value={walletAddress} style={{
                    flex: 1, padding: '10px 12px', background: '#12121c',
                    border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '14px'
                  }} />
                  <button
                    onClick={() => { navigator.clipboard.writeText(walletAddress); showToast(t('walletAddressCopied'), 'success'); }}
                    style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {t('copy')}
                  </button>
                </div>

                {/* 二维码 */}
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <span style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>{t('scanQrCode')}</span>
                  <img src="/qrcode.png" alt="QR" style={{ width: '160px', height: '160px', border: '4px solid #fff', borderRadius: '8px' }} />
                  <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#666' }}>{t('useUsdtTrc20')}</span>
                </div>
              </div>

              {/* 购买金额 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>{t('paymentAmount')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="text" readOnly value={`$${plans.find(p => p.id === selectedPlan)?.price || 0}`} style={{
                    flex: 1, padding: '10px 12px', background: '#12121c',
                    border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '14px'
                  }} />
                  <span style={{ color: '#666', fontSize: '13px' }}>USDT</span>
                </div>
              </div>

              {/* 支付截图上传 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>{t('uploadPaymentScreenshot')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                  <button type="button" onClick={() => fileInputRef.current?.click()} style={{
                    padding: '8px 16px', background: '#2d2d44', color: '#ccc',
                    border: '1px solid #444', borderRadius: '8px', cursor: 'pointer'
                  }}>
                    {t('chooseFile')}
                  </button>
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    {paymentData.screenshot ? paymentData.screenshot.name : t('noFileSelected')}
                  </span>
                </div>
                {paymentData.screenshot && (
                  <div style={{ marginTop: '12px' }}>
                    <img src={URL.createObjectURL(paymentData.screenshot)} alt="" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '8px', border: '1px solid #333' }} />
                  </div>
                )}
              </div>

              {/* 备注 */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>{t('noteOptional')}</label>
                <textarea name="remark" value={paymentData.remark} onChange={handleInputChange} rows="3"
                  placeholder={t('noteOptionalPlaceholder')} style={{
                    width: '100%', padding: '10px 12px', background: '#12121c',
                    border: '1px solid #333', borderRadius: '8px', color: '#fff',
                    fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
                  }}
                ></textarea>
              </div>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid #2d2d44', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={handleCancelPayment} style={{
                padding: '10px 24px', background: '#2d2d44', color: '#ccc',
                border: '1px solid #444', borderRadius: '8px', cursor: 'pointer'
              }}>
                {t('cancel')}
              </button>
              <button onClick={handlePaymentSubmit} disabled={submitting} style={{
                padding: '10px 24px', background: submitting ? '#555' : '#3b82f6', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer'
              }}>
                {submitting ? t('processing') : t('submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 提交成功弹窗 */}
      {showSuccessModal && (
        <div className="modal-overlay open" onClick={() => { setShowSuccessModal(false); }} style={{ zIndex: 99999 }}>
          <div className="modal" style={{ width: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-hd">
              <span className="modal-title">{t('paymentSubmitted')}</span>
              <span className="modal-close" onClick={() => setShowSuccessModal(false)}>×</span>
            </div>
            <div className="modal-body" style={{ padding: '20px', textAlign: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="var(--green)" style={{ marginBottom: '12px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p style={{ color: 'var(--t2)', fontSize: '14px', lineHeight: 1.6 }}>
                {t('paymentSubmittedMessage')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowSuccessModal(false)}>
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast通知 */}
      {toast.visible && (
        <div className={`cloak-toast ${toast.type === 'error' ? 'toast-error' : 'toast-success'}`}>
          {toast.type === 'success' ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="cloak-toast-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="cloak-toast-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default CloakPricing;
